import React, { useState, useEffect } from "react";
import "twin.macro";
import { Endorsement, Measure, Model } from "@madie/madie-models";
import useMeasureServiceApi from "../../../../api/useMeasureServiceApi";
import "styled-components/macro";
import {
  AutoComplete,
  Button,
  MadieDiscardDialog,
  Toast,
  TextField,
  ReadOnlyTextField,
  Select,
} from "@madie/madie-design-system/dist/react";
import {
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import { MeasureSchemaValidator } from "../../../../validations/MeasureSchemaValidator";
import useFormikResetOnEvent from "../../../common/useFormikResetOnEvent";
import {
  measureStore,
  routeHandlerStore,
  checkUserCanEdit,
} from "@madie/madie-util";
import { Box } from "@mui/system";
import {
  parseContent,
  synchingEditorCqlContent,
  validateContent,
} from "@madie/madie-editor";
import "./MeasureInformation.scss";
import CmsIdentifier from "../cmsIdentifier/CmsIdentifier";
import { QdmMeasureSchemaValidator } from "../../../../validations/QDMMeasureSchemaValidator";
import useQdmElmTranslationServiceApi from "../../../../api/useQdmElmTranslationServiceApi";
import useFhirElmTranslationServiceApi from "../../../../api/useFhirElmTranslationServiceApi";
import GenerateCmsIdConfirmationDialog from "../cmsIdentifier/GenerateCmsIdConfirmationDialog";
import _ from "lodash";

interface measureInformationForm {
  versionId: string;
  measureName: string;
  cqlLibraryName: string;
  ecqmTitle: string;
  measurementPeriodStart: Date;
  measurementPeriodEnd: Date;
  measureId: string;
  cmsId: string;
  experimental: boolean;
  endorsements: Array<Endorsement>;
  endorsementId: string;
  intendedVenue?: any;
}

interface MeasureInformationProps {
  setErrorMessage: Function;
}

export default function MeasureInformation(props: MeasureInformationProps) {
  const { setErrorMessage } = props;
  const measureServiceApi = useMeasureServiceApi();
  const qdmElmTranslationService = useQdmElmTranslationServiceApi();
  const fhirElmTranslationService = useFhirElmTranslationServiceApi();

  const [endorsers, setEndorsers] = useState<string[]>();
  const [endorsementIdRequired, setEndorsementIdRequired] = useState<boolean>();
  const { updateMeasure } = measureStore;
  const [measure, setMeasure] = useState<any>(measureStore.state);
  const [translatorVersion, setTranslatorVersion] = useState("");

  const getTranslatorVersion = async (model, draft) => {
    if (model.includes("QDM")) {
      qdmElmTranslationService.fetchTranslatorVersion(draft).then((data) => {
        setTranslatorVersion(data);
      });
    } else {
      fhirElmTranslationService.fetchTranslatorVersion(draft).then((data) => {
        setTranslatorVersion(data);
      });
    }
  };
  useEffect(() => {
    // if we have a measure loaded
    if (measure?.model) {
      const model = measure.model;
      const draft = measure?.measureMetaData?.draft;
      // if it's in draft we request
      if (draft) {
        getTranslatorVersion(model, draft);
      } else {
        const elmJson = JSON.parse(measure.elmJson);
        const annotations = elmJson?.library?.annotation;
        if (annotations?.length) {
          const found = annotations.find((e) =>
            e.hasOwnProperty("translatorVersion")
          );
          if (found) {
            setTranslatorVersion(found.translatorVersion);
          }
        } else {
          getTranslatorVersion(model, draft);
        }
      }
    }
  }, [measure?.model]);
  const transLatorLable = measure?.measureMetaData?.draft
    ? `Currently using CQL to ELM Translator Version`
    : `Versioned with CQL to ELM Translator Version`;

  useEffect(() => {
    const subscription = measureStore.subscribe(setMeasure);
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  const row = {
    display: "flex",
    flexDirection: "row",
  };
  const spaced = {
    marginBottom: "32px",
  };
  const gap = {
    columnGap: "24px",
    "& > * ": {
      flex: 1,
    },
  };
  const formRow = Object.assign({}, row, spaced);
  const formRowGapped = Object.assign({}, formRow, gap);

  // Toast utilities
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<string>("danger");

  const intendedVenueValues = [
    {
      code: "ec",
      codeSystem:
        "http://hl7.org/fhir/us/cqfmeasures/CodeSystem/intended-venue-codes",
      display: "EC",
      definition:
        "An eligible clinician is a clinician who is eligible to participate in a quality measurement intiative.",
    },
    {
      code: "eh",
      codeSystem:
        "http://hl7.org/fhir/us/cqfmeasures/CodeSystem/intended-venue-codes",
      display: "EH",
      definition:
        "An eligible hospital is an acute care facility that is eligible to participate in a quality measurement initiative.",
    },
  ];

  const intendedVenueOptions = [
    "-",
    "Eligible Clinician (EC)",
    "Eligible Hospital (EH)",
  ];

  const selectedIntendedVenueValue = (selectedIntendedVenue: string) => {
    if (selectedIntendedVenue?.includes("(EC)")) {
      return intendedVenueValues[0];
    }
    if (selectedIntendedVenue?.includes("EH")) {
      return intendedVenueValues[1];
    }
    return null;
  };

  const displayIntendedVenue = (intendedVenueCode: string) => {
    if (intendedVenueCode === "ec") {
      return intendedVenueOptions[1];
    }
    if (intendedVenueCode === "eh") {
      return intendedVenueOptions[2];
    }
    return intendedVenueOptions[0];
  };

  // our initial values are taken from the measure we subscribe to
  const INITIAL_VALUES = {
    measurementPeriodStart: measure?.measurementPeriodStart,
    measurementPeriodEnd: measure?.measurementPeriodEnd,
    measureName: measure?.measureName,
    cqlLibraryName: measure?.cqlLibraryName,
    ecqmTitle: measure?.ecqmTitle,
    versionId:
      measure?.versionId === null || measure?.versionId === undefined
        ? measure?.id
        : measure?.versionId,
    measureId: measure?.measureSetId,
    experimental:
      measure?.measureMetaData?.experimental ||
      (measure?.model === Model.QDM_5_6 ? null : false),
    endorsements: measure?.measureMetaData?.endorsements || [],
    endorsementId: measure?.measureMetaData?.endorsements?.[0]?.endorsementId,
    intendedVenue:
      displayIntendedVenue(measure?.measureMetaData?.intendedVenue?.code) ||
      null,
  } as measureInformationForm;

  const schema =
    measure?.model === "QI-Core v4.1.1"
      ? MeasureSchemaValidator
      : QdmMeasureSchemaValidator;

  const formik = useFormik({
    initialValues: { ...INITIAL_VALUES },
    validationSchema: schema,
    enableReinitialize: true, // formik will auto set initial variables whenever measure delivers new results
    onSubmit: async (values: measureInformationForm) =>
      await handleSubmit(values),
  });
  useFormikResetOnEvent(formik);
  const { resetForm } = formik;
  // tell our routehandler no go
  const { updateRouteHandlerState } = routeHandlerStore;
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [cmsIdDialogOpen, setCmsIdDialogOpen] = useState(false);

  useEffect(() => {
    updateRouteHandlerState({
      canTravel: !formik.dirty,
      pendingRoute: "",
    });
  }, [formik.dirty]);

  const goBackToNav = (e) => {
    if (e.shiftKey && e.keyCode == 9) {
      e.preventDefault();
      document.getElementById("sideNavMeasureInformation").focus();
    }
  };

  // fetch endorsers DB using measure service and sorts alphabetically
  useEffect(() => {
    measureServiceApi
      .getAllEndorsers()
      .then((response) => {
        const endorsers = response
          .sort((a, b) =>
            a.endorserOrganization.localeCompare(b.endorserOrganization)
          )
          .map((element) => element.endorserOrganization);
        setEndorsers(endorsers);
        formik.validateForm();
      })
      .catch(() => {
        const message = `Error fetching endorsers`;
        setErrorMessage(message);
      });
  }, []);

  const canEdit = checkUserCanEdit(
    measure?.measureSet?.owner,
    measure?.measureSet?.acls,
    measure?.measureMetaData?.draft
  );

  const handleEndorserChange = (selectedValue: string) => {
    if (selectedValue === "" || selectedValue === "-") {
      selectedValue = null;
    }
    const newList: Endorsement[] = [];
    newList.push({
      endorser: selectedValue,
    });

    formik.setFieldValue("endorsements", newList);
    if (selectedValue === null) {
      setEndorsementIdRequired(false);
      formik.setFieldValue("endorsementId", "");
      formik.setFieldValue("endorserSystemId", null);
    } else {
      setEndorsementIdRequired(true);
      formik.setFieldValue(
        "endorserSystemId",
        "https://madie.cms.gov/measure/nqfId"
      );
    }
  };

  const onToastClose = () => {
    setToastType("danger");
    setToastMessage("");
    setToastOpen(false);
  };
  const handleToast = (type, message, open) => {
    setToastType(type);
    setToastMessage(message);
    setToastOpen(open);
  };

  const validateEndorser = (
    endorser: string,
    endorsementId: string
  ): boolean => {
    // empty string needs to be considered
    if (endorser && endorsementId === "") {
      handleToast("danger", "Endorser Number is Required", true);
      return false;
    }
    if (!!!endorser && endorsementId !== "") {
      handleToast(
        "danger",
        "Endorser Organization is set to None, Endorser Number must not contain a value",
        true
      );
      return false;
    }
    if (endorsementId) {
      const stripped = endorsementId.replace(/^[0-9a-zA-Z]*$/, "");
      if (stripped && stripped.length > 0) {
        handleToast("danger", "Endorser Number must be alpha numeric", true);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (values) => {
    let endorsersValid = true;
    // we only want to validate this field if it's not an empty array.
    if (values.endorsements.length > 0) {
      endorsersValid = validateEndorser(
        values.endorsements[0]?.endorser,
        values.endorsementId
      );
    }
    if (endorsersValid) {
      const using = measure?.model.split(" v");
      const updatedCqlOb = await synchingEditorCqlContent(
        "",
        measure?.cql,
        values.cqlLibraryName,
        measure?.cqlLibraryName,
        measure?.version,
        using[0],
        using[1],
        "measureInformation"
      );
      // Generate updated ELM when Library name is modified
      //  and there are no CQL errors.
      if (INITIAL_VALUES.cqlLibraryName !== values.cqlLibraryName) {
        if (updatedCqlOb && updatedCqlOb.cql?.trim()) {
          const cqlErrors = parseContent(updatedCqlOb.cql);
          const { errors, translation } = await validateContent(
            updatedCqlOb.cql,
            true
          );
          if (cqlErrors.length === 0 && errors.length === 0) {
            var updatedElm = JSON.stringify(translation);
          }
        }
      }

      const newMeasure: Measure = {
        ...measure,
        versionId: values.versionId,
        measureName: values.measureName,
        cqlLibraryName: values.cqlLibraryName,
        ecqmTitle: values.ecqmTitle,
        cql: updatedCqlOb?.cql,
        elmJson: updatedElm ? updatedElm : measure.elmJson,
        measureId: values.measureSetId,
        measureMetaData: {
          ...measure?.measureMetaData,
          experimental: values.experimental,
          endorsements: [
            {
              endorser: values.endorsements[0]?.endorser || "",
              endorsementId: values.endorsementId || "",
              endorserSystemId:
                values.endorsements[0]?.endorserSystemId || null,
            },
          ],
          intendedVenue: selectedIntendedVenueValue(values?.intendedVenue),
        },
      };
      measureServiceApi
        .updateMeasure(newMeasure)
        .then(() => {
          handleToast(
            "success",
            "Measurement Information Updated Successfully",
            true
          );
          // updating measure will propagate update state site wide.
          updateMeasure(newMeasure);
        })
        // update to alert
        .catch((err) => {
          setErrorMessage(err?.response?.data?.message?.toString());
        });
    }
  };

  function formikErrorHandler(name: string, isError: boolean) {
    if (formik.touched[name] && formik.errors[name]) {
      return `${formik.errors[name]}`;
    }
  }

  // we create a state to track current focus. We only display helper text on focus and remove current focus on blur
  const [focusedField, setFocusedField] = useState("");
  const onBlur = (field) => {
    setFocusedField("");
    formik.setFieldTouched(field);
  };
  const onFocus = (field) => {
    setFocusedField(field);
  };

  const createCmsId = async (measureSetId: string) => {
    try {
      const updatedMeasureSet = await measureServiceApi.createCmsId(
        measureSetId
      );
      const newMeasure: Measure = {
        ...measure,
        measureSet: {
          ...measure?.measureSet,
          cmsId: updatedMeasureSet.cmsId,
        },
      };
      updateMeasure(newMeasure);
    } catch (error) {
      handleToast("danger", "Failed to create CMS ID.", true);
    }
  };

  return (
    <form
      id="measure-details-form"
      onSubmit={formik.handleSubmit}
      data-testid="measure-information-form"
    >
      <div className="content">
        <div className="subTitle">
          <h2>Name, Version & ID</h2>
          <Typography
            style={{
              fontSize: 14,
              fontWeight: 300,
              fontFamily: "Rubik",
              float: "right",
            }}
          >
            <span style={{ color: "#D92F2F", marginRight: 3 }}>*</span>
            Indicates required field
          </Typography>
        </div>
        <Box sx={formRowGapped}>
          <TextField
            onFocus={() => onFocus("measureName")}
            placeholder="Measure Name"
            required
            disabled={!canEdit}
            label="Measure Name"
            id="measureName"
            inputProps={{
              "data-testid": "measure-name-input",
              "aria-required": "true",
            }}
            helperText={
              (formik.touched["measureName"] ||
                focusedField === "measureName") &&
              formikErrorHandler("measureName", true)
            }
            data-testid="measure-name-text-field"
            size="small"
            onKeyDown={goBackToNav}
            error={
              formik.touched.measureName && Boolean(formik.errors.measureName)
            }
            {...formik.getFieldProps("measureName")}
            onBlur={() => {
              onBlur("measureName");
            }}
            maxLength={500}
          />
          <TextField
            onFocus={() => onFocus("cqlLibraryName")}
            placeholder="Enter CQL Library Name"
            required
            disabled={!canEdit}
            label="Measure CQL Library Name"
            id="cqlLibraryName"
            data-testid="cql-library-name"
            inputProps={{
              "data-testid": "cql-library-name-input",
              "aria-required": "true",
            }}
            {...formik.getFieldProps("cqlLibraryName")}
            helperText={formik.errors["cqlLibraryName"]}
            size="small"
            error={Boolean(formik.errors.cqlLibraryName)}
            onBlur={() => {
              onBlur("cqlLibraryName");
            }}
            maxLength={64}
          />
        </Box>
        <Box sx={formRowGapped}>
          <ReadOnlyTextField
            tabIndex={0}
            placeholder="Measure ID"
            label="Measure Id"
            id="measureId"
            data-testid="measure-id-text-field"
            inputProps={{ "data-testid": "measure-id-input" }}
            helperText={formikErrorHandler("measureId", true)}
            size="small"
            error={formik.touched.measureId && Boolean(formik.errors.measureId)}
            {...formik.getFieldProps("measureId")}
          />
          <ReadOnlyTextField
            tabIndex={0}
            placeholder="Version ID"
            label="Version ID"
            id="versionId"
            data-testid="version-id-text-field"
            inputProps={{ "data-testid": "version-id-input" }}
            helperText={formikErrorHandler("versionId", true)}
            size="small"
            error={formik.touched.versionId && Boolean(formik.errors.versionId)}
            {...formik.getFieldProps("versionId")}
          />
        </Box>

        <Box sx={formRowGapped}>
          <TextField
            placeholder="eCQM Name"
            required
            disabled={!canEdit}
            label="eCQM Abbreviated Title"
            id="ecqmTitle"
            data-testid="ecqm-text-field"
            inputProps={{
              "data-testid": "ecqm-input",
              "aria-required": "true",
            }}
            helperText={formikErrorHandler("ecqmTitle", true)}
            size="small"
            error={formik.touched.ecqmTitle && Boolean(formik.errors.ecqmTitle)}
            {...formik.getFieldProps("ecqmTitle")}
            maxLength={32}
          />

          <CmsIdentifier
            canEdit={canEdit}
            label="CMS ID"
            cmsId={measure?.measureSet?.cmsId}
            model={measure?.model}
            onClick={() => setCmsIdDialogOpen(true)}
          />
        </Box>

        {measure?.model !== Model.QDM_5_6 && (
          <Box sx={formRowGapped}>
            <FormControlLabel
              control={
                <Checkbox
                  {...formik.getFieldProps("experimental")}
                  checked={formik.values.experimental}
                  disabled={!canEdit}
                  name="experimental"
                  id="experimental"
                  data-testid="experimental"
                />
              }
              label="Experimental"
            />
          </Box>
        )}

        <Box sx={formRowGapped}>
          <AutoComplete
            id="endorser"
            dataTestId="endorser"
            label="Endorsing Organization"
            placeholder="-"
            disabled={!canEdit}
            error={formik.touched.endorsements && formik.errors["endorsements"]}
            helperText={
              formik.touched.endorsements && formik.errors["endorsements"]
            }
            options={endorsers}
            value={formik.values.endorsements.map(
              (selected) => selected?.endorser
            )}
            onChange={(_event: any, selectedValues: any | null) => {
              handleEndorserChange(selectedValues);
            }}
            onKeyDown={goBackToNav}
          />
          <TextField
            onFocus={() => onFocus("endorsementId")}
            placeholder="-"
            required
            disabled={!canEdit || !endorsementIdRequired}
            label="Endorsement #"
            id="endorsementId"
            inputProps={{
              "data-testid": "endorsement-number-input",
              "aria-required": "true",
            }}
            helperText={
              (formik.touched["endorsementId"] ||
                focusedField === "endorsementId") &&
              formikErrorHandler("endorsementId", true)
            }
            data-testid="endorsement-number-text-field"
            size="small"
            onKeyDown={goBackToNav}
            error={
              formik.touched.endorsements && Boolean(formik.errors.endorsements)
            }
            {...formik.getFieldProps("endorsementId")}
            onBlur={() => {
              onBlur("endorsementId");
            }}
          />
          <div />
          <div />
        </Box>
        <Box sx={formRowGapped}>
          <ReadOnlyTextField
            value={translatorVersion}
            label={transLatorLable}
            tabIndex={0}
            placeholder="Translator Version"
            id="translator-label"
            data-testid="translator-version-text-field"
            inputProps={{ "data-testid": "translator-version-input" }}
            size="small"
          />
        </Box>

        {measure?.model !== Model.QDM_5_6 && (
          <div tw="mb-4 w-1/2">
            <Select
              placeHolder={"-"}
              label="Intended Venue"
              id="intended-venue"
              inputProps={{
                "data-testid": "intended-venue-input",
              }}
              data-testid="intended-venue"
              value={`${formik.values?.intendedVenue}`}
              disabled={!canEdit}
              size="small"
              SelectDisplayProps={{
                "aria-required": "true",
              }}
              onChange={(e) =>
                formik.setFieldValue("intendedVenue", e.target.value)
              }
              options={intendedVenueOptions.map((intendedVenue, i) => (
                <MenuItem
                  key={`${intendedVenue}-${i}`}
                  data-testid={`${_.camelCase(intendedVenue)}-option`}
                  value={intendedVenue}
                >
                  {intendedVenue}
                </MenuItem>
              ))}
            />
          </div>
        )}
      </div>
      {canEdit && (
        <div className="form-actions">
          <Button
            onClick={() => setDiscardDialogOpen(true)}
            variant="outline"
            data-testid="cancel-button"
            disabled={!formik.dirty}
            style={{ marginTop: 20, float: "right", marginRight: 32 }}
          >
            Discard Changes
          </Button>
          <Button
            variant="cyan"
            type="submit"
            data-testid="measurement-information-save-button"
            disabled={!(formik.isValid && formik.dirty)}
            style={{ marginTop: 20, float: "right" }}
          >
            Save
          </Button>
        </div>
      )}
      <Toast
        toastKey="measure-information-toast"
        aria-live="polite"
        toastType={toastType}
        testId={
          toastType === "danger"
            ? "edit-measure-information-generic-error-text"
            : "edit-measure-information-success-text"
        }
        open={toastOpen}
        message={toastMessage}
        onClose={onToastClose}
        autoHideDuration={10000}
        closeButtonProps={{
          "data-testid": "close-error-button",
        }}
      />
      <MadieDiscardDialog
        open={discardDialogOpen}
        onContinue={() => {
          resetForm();
          setDiscardDialogOpen(false);
        }}
        onClose={() => setDiscardDialogOpen(false)}
      />

      <GenerateCmsIdConfirmationDialog
        open={cmsIdDialogOpen}
        onContinue={async () => {
          await createCmsId(measure?.measureSet?.measureSetId);
          setCmsIdDialogOpen(false);
        }}
        onClose={() => setCmsIdDialogOpen(false)}
      />
    </form>
  );
}
